import { action } from 'mobx'
import { task } from 'mobx-task'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { DocumentType } from '@/features/documents/DocumentType'
import { Id } from '@/infra/id'
import {
  type CreateInventoryItemParams,
  type InventoryStore,
} from '@/features/inventory/InventoryStore'
import { InventoryItemDialogViewModelBase } from '@/features/inventory/admin/inventory-item-dialog/InventoryItemDialogViewModelBase'
import { type RefreshInventoryItems } from '@/features/inventory/admin/InventorySearchViewModel'

/**
 * Adds a service item to the global inventory.
 */
export class CreateInventoryItemDialogViewModel extends InventoryItemDialogViewModelBase {
  constructor(
    private readonly _inventoryStore: InventoryStore,
    private readonly _refreshItems: RefreshInventoryItems,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    getDocumentTypes: () => DocumentType[],
  ) {
    super(getDocumentTypes)
  }

  /**
   * Creates the inventory item
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const payload: CreateInventoryItemParams = {
      name: this.name,
      description: this.description,
      courtesyAmount: this.courtesyQuantity,
      pricing: this.pricingVm.toValues(),
      workspace: Id.none,
      supportingItems: this.supportingItems,
      hideFromPro: this.hideFromPro,
      typeSpecification: this.getInventoryItemTypeSpecification(),
    }

    await this._inventoryStore
      .createInventoryItem(payload)
      .catch(this._createAlertErrorHandler('Adding the item failed'))
    await this._refreshItems(Workspace.none)

    this._newAlert()
      .withState('success')
      .saying('Success', 'Item added.')
      .autoDismiss()
      .show()

    this.reset()
    this.modal.close()
  })

  @action.bound
  open() {
    this.reset()
    this.modal.show()
  }
}
